import React, { useEffect, useMemo, useState, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Flex, Text } from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import ECheckbox from "components/ECheckbox";
import ERadioGroup from "components/ERadioGroup";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import EDateRangePicker from "components/EDateRangePicker";
import {
  RADIO_DATA_VALID_PERIOD,
  RADIO_DATA_TARGET_RENEWAL_CYCLE,
  PER_PAGE_DEFAULT_EMPLOYEES,
  DOCUMENT_TYPE_INDIVIDUAL_TRAINING,
  COURSE_TYPE_ASSIGN_RANDOM,
  FORMAT_DATE,
  PATHNAME,
} from "constants";
import DeleteButton from "components/Buttons/DeleteButton";
import { useModalState } from "hooks/useModalState";
import ESelectWithSearch from "components/ESelectWithSearch";
import { useGetListDocument } from "hooks/useDocument";
import ModalAlert from "components/modals/AlertDialog";
import {
  useUpdateCourse,
  usePushJobManual,
  useDeleteCourse,
  getDetailCourse,
} from "hooks/useCourse";
import GreenButton from "components/Buttons/GreenButton";
import DisableButton from "../../components/Buttons/DisableButton";
import useSearchEmployee from "hooks/useSearchEmployee";
import FullPageLoader from "components/Loading/FullPageLoading";
import ESelect from "components/ESelect";
import { useCreateCourse } from "hooks/useCourse";
import { useListTargetGroup } from "hooks/useTargetGroup";
import CourseEmployeeInfo from "./CourseEmployeeInfo";
import { useEmployeeStore } from "store/employeeStore";

const IndividualCourseForm = () => {
  const location = useLocation();
  const history = useHistory();
  const { courseId } = useParams();
  const isCreatePage = location.pathname === PATHNAME.CREATE_INDIVIDUAL_COURSE;

  const { modalState, open, close } = useModalState({
    modalEmployees: false,
    modalAlert: false,
  });

  // const [tempDocument, setTempDocument] = useState();
  // const [isClearDocument, setIsClearDocument] = useState();
  const [detailCourseIndividual, setDetailCourseIndividual] = useState();
  const documentIdSetRef = useRef(false);

  const { data: listTargetGroup } = useListTargetGroup({ type: 2 });

  const {
    data: dataCourse,
    loading: isLoadingDetailCourse,
    refetch,
  } = getDetailCourse(courseId, true);
  const {
    data: dataDocuments,
    loading: isLoadingListDocument,
  } = useGetListDocument({ page: 1 });

  const schema = yup.object().shape({
    document_id: yup.object().nullable(),
    validity_from: yup.string().nullable(),
    validity_to: yup
      .string()
      .nullable()
      .test("validity_from", t("messages.date_end"), (value, context) => {
        return !(
          value && new Date(value) < new Date(context.parent.validity_from)
        );
      }),
    // group_type: yup.string().required(t("messages.required")),
  });
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      target_group_id: "",
      is_required_all: false,
      effective_time: "1",
      validity_from: "",
      validity_to: "",
      employees: "",
      target_renewal_cycle: "1",
      document_id: null,
      group_type: "",
    },
    resolver: yupResolver(schema),
  });

  const {
    selectedEmployeeIds,
    allEmployees,
    paramsListEmployee,
  } = useEmployeeStore((state) => state);
  const {
    resetAllEmployee,
    setSelectedEmployeeIds,
    setParamsListEmployee,
    setSearchedEmployees,
    setAllEmployees,
  } = useEmployeeStore();
  const {
    fetchAllDataEmployees,
  } = useSearchEmployee(
    {
      is_open: modalState.modalEmployees,
    },
    setValue
  );

  const mutationCreateCourse = useCreateCourse(true);
  const mutationUpdateCourse = useUpdateCourse();
  const mutationDeleteCourse = useDeleteCourse(true);
  const mutationPushJobManual = usePushJobManual({ isPersonal: true });

  const [validateDocument, setValidateDocument] = useState(false);
  const [targetGroups, setTargetGroups] = useState([]);

  const documentOptions = useMemo(() => {
    return [{ label: " 選択して下さい", value: "" }].concat(
      dataDocuments?.result?.data
        ?.filter((itm) => {
          return itm.type === DOCUMENT_TYPE_INDIVIDUAL_TRAINING && itm.target_group_id == watch('target_group_id');
        })
        .map((item) => {
          return {
            value: item.id,
            label: `${item.course_code}  ${item.name}`,
          };
        })
    )
  }, [dataDocuments?.result?.data, watch('target_group_id')])

  useEffect(() => {
    // Only reset if we don't have sessionStorage data (to preserve selections when returning from assign page)
    if (isCreatePage) {
      const dataCreateCourseIndividualJson = sessionStorage.getItem("dataCreateCourseIndividual");
      if (!dataCreateCourseIndividualJson) {
        resetAllEmployee();
      }
    } else {
      const useSelectedListJson = sessionStorage.getItem("useSelectedList");
      if (!useSelectedListJson) {
        resetAllEmployee();
      }
    }
    fetchAllDataEmployees();
  }, []);

  const onPushManual = (id) => {
    if (getValues("document_id")) {
      mutationPushJobManual.mutate({ id: id });
      clearErrors("document_id");
      return;
    }
    setError("document_id", { message: t("messages.required") });
  };

  const onchangeAllCompany = (event) => {
    if (event.target.checked) {
      setValue("employees", t("pages.course_form.all_people"));
    } else {
      setValue("employees", []);
      setSelectedEmployeeIds([]);
    }
  };

  // delete data user_elected when load page
  window.addEventListener("load", () => {
    sessionStorage.removeItem("useSelectedList");
    sessionStorage.removeItem("courseId");
    sessionStorage.removeItem("dataCreateCourseIndividual");
  });

  // Load data for create page from sessionStorage
  useEffect(() => {
    if (isCreatePage) {
      // Reset the ref when loading new data
      documentIdSetRef.current = false;
      
      const dataCreateCourseIndividualJson = sessionStorage.getItem(
        "dataCreateCourseIndividual"
      );

      if (!dataCreateCourseIndividualJson) return;

      const dataCreateCourseIndividual = JSON.parse(
        dataCreateCourseIndividualJson
      );

      // check if user_selected is data
      const selectedUser = dataCreateCourseIndividual?.useSelectedList || [];

      if (!dataCreateCourseIndividual?.is_required_all && selectedUser.length > 0) {
        const idsSelected = selectedUser.map((item) => item.id);
        setSelectedEmployeeIds(idsSelected);
        setSearchedEmployees(selectedUser);
        setAllEmployees(selectedUser);
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: JSON.stringify(idsSelected),
          id_exception_list: null,
          no_data: 0,
          isPaging: 0,
        });
      }

      if (dataCreateCourseIndividual?.is_required_all) {
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: null,
          id_exception_list: null,
          no_data: 0,
          isPaging: 1,
        });
      }

      reset({
        target_group_id: dataCreateCourseIndividual?.target_group_id,
        is_required_all: !!dataCreateCourseIndividual?.is_required_all,
        effective_time: dataCreateCourseIndividual?.effective_time?.toString(),
        validity_from: dataCreateCourseIndividual?.validity_from
          ? new Date(dataCreateCourseIndividual?.validity_from)
          : null,
        validity_to: dataCreateCourseIndividual?.validity_to
          ? new Date(dataCreateCourseIndividual?.validity_to)
          : null,
        // hide
        user_selected_list: JSON.stringify(selectedUser),
        employees: selectedUser.length > 0 ? selectedUser.map((i) => i.name).join(", ") : "",
        // user_assigned_list: dataCreateCourseIndividual?.user_assigned_list ?? [],
        // employees: JSON.parse(dataCreateCourseIndividual?.user_assigned_list ?? '[]')
        //   .map((i) => i.name)
        //   .join(", "),
        target_renewal_cycle: dataCreateCourseIndividual?.target_renewal_cycle?.toString(),
        document_id: null, // Will be set in separate useEffect after documentOptions is ready
        group_type: dataCreateCourseIndividual?.group_type,
      });
    }
  }, [isCreatePage]);

  // Format and set document_id from sessionStorage after target_group_id is set and documentOptions is ready
  useEffect(() => {
    if (!isCreatePage || documentIdSetRef.current) return;

    const currentTargetGroupId = watch('target_group_id');
    if (!currentTargetGroupId) return;

    const dataCreateCourseIndividualJson = sessionStorage.getItem(
      "dataCreateCourseIndividual"
    );
    if (!dataCreateCourseIndividualJson) return;

    const dataCreateCourseIndividual = JSON.parse(dataCreateCourseIndividualJson);
    
    // Verify target_group_id matches
    const storageTargetGroupId = dataCreateCourseIndividual?.target_group_id;
    if (String(currentTargetGroupId) !== String(storageTargetGroupId)) {
      return;
    }

    // Wait for documentOptions to be ready (must have actual documents, not just placeholder)
    // documentOptions depends on target_group_id, so it should be recalculated after target_group_id is set
    // Also check if dataDocuments is loaded
    if (!dataDocuments?.result?.data || !documentOptions || documentOptions.length <= 1) {
      return;
    }

    const documentIdFromStorage = dataCreateCourseIndividual?.document_id;
    if (!documentIdFromStorage) return;

    // Extract the document ID value from storage
    let documentIdValue = null;
    if (documentIdFromStorage && typeof documentIdFromStorage === 'object') {
      // Object format: {value: ..., label: ...} from getValues()
      documentIdValue = documentIdFromStorage.value || documentIdFromStorage.id;
    } else if (documentIdFromStorage) {
      // Direct value: number or string
      documentIdValue = documentIdFromStorage;
    }

    if (!documentIdValue || documentIdValue === '') return;

    // Find the document in documentOptions
    const formattedDocumentId = documentOptions.find((option) => {
      if (!option) return false;
      // Compare as strings to handle number/string mismatch
      return String(option.value) === String(documentIdValue);
    });

    // Debug logging (can be removed later)
    if (!formattedDocumentId) {
      console.log('Document ID not found in documentOptions:', {
        documentIdValue,
        documentOptions: documentOptions.map(opt => ({ value: opt.value, label: opt.label })),
        documentIdFromStorage
      });
    }

    // Set document_id if we found a match
    if (formattedDocumentId) {
      // Use setTimeout to ensure documentOptions is fully ready
      setTimeout(() => {
        setValue("document_id", formattedDocumentId, { shouldValidate: false });
        documentIdSetRef.current = true;
      }, 0);
    }
  }, [isCreatePage, watch('target_group_id'), documentOptions, setValue]);

  //set value to form when dataCourse is fetched (for edit page)
  useEffect(() => {
    if (dataCourse?.result?.data && !isCreatePage) {
      const detailCourse = dataCourse.result.data;
      setDetailCourseIndividual(detailCourse);

      const useSelectedListJson = sessionStorage.getItem("useSelectedList");
      let useSelectedList = null;
      try {
        useSelectedList = useSelectedListJson ? JSON.parse(useSelectedListJson) : null;
      } catch (e) {
        useSelectedList = null;
      }

      // check if user_selected is data
      let selectedUser = [];
      try {
        selectedUser = useSelectedList && Array.isArray(useSelectedList)
          ? useSelectedList
          : (detailCourse?.user_selected_list 
              ? (Array.isArray(JSON.parse(detailCourse.user_selected_list)) 
                  ? JSON.parse(detailCourse.user_selected_list) 
                  : [])
              : []);
      } catch (e) {
        selectedUser = [];
      }

      if (!detailCourse?.is_required_all) {
        const idsSelected = selectedUser.map((item) => item.id);
        setSelectedEmployeeIds(idsSelected);
        setSearchedEmployees(selectedUser);
        setAllEmployees(selectedUser);
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: JSON.stringify(idsSelected),
          id_exception_list: null,
          no_data: 0,
          isPaging: 0,
        });
      }

      if (detailCourse?.is_required_all) {
        setParamsListEmployee({
          ...paramsListEmployee,
          id_selected_list: null,
          id_exception_list: null,
          no_data: 0,
          isPaging: 1,
        });
      }

      reset({
        target_group_id: detailCourse?.target_group_id,
        is_required_all: !!detailCourse?.is_required_all,
        effective_time: detailCourse?.effective_time?.toString(),
        validity_from: detailCourse?.validity_from
          ? new Date(detailCourse?.validity_from)
          : null,
        validity_to: detailCourse?.validity_to
          ? new Date(detailCourse?.validity_to)
          : null,
        // hide
        user_selected_list: useSelectedListJson
          ? useSelectedListJson
          : detailCourse?.user_selected_list ?? [],
        employees: Array.isArray(useSelectedList)
          ? useSelectedList.map((i) => i.name).join(", ")
          : selectedUser.map((i) => i.name).join(", "),
        // user_assigned_list: detailCourse?.user_assigned_list ?? [],
        // employees: JSON.parse(detailCourse?.user_assigned_list ?? '[]')
        //   .map((i) => i.name)
        //   .join(", "),
        target_renewal_cycle: detailCourse?.target_renewal_cycle?.toString(),
        document_id: documentOptions?.find((i) => {
          if (i) return i.value === detailCourse.document_id;
        }),
        group_type: COURSE_TYPE_ASSIGN_RANDOM[detailCourse?.type],
      });
      // setTempDocument(
      //   documentOptions?.find((i) => {
      //     if (i) return i.value === detailCourse.document_id;
      //   })
      // );
    }
  }, [dataCourse?.result?.data, isCreatePage]);

  // Check for updated sessionStorage data when returning from assign page (for edit page)
  useEffect(() => {
    if (!isCreatePage && courseId) {
      const checkAndUpdateSelectedEmployees = () => {
        const useSelectedListJson = sessionStorage.getItem("useSelectedList");
        if (useSelectedListJson) {
          try {
            const useSelectedList = JSON.parse(useSelectedListJson);
            if (useSelectedList && Array.isArray(useSelectedList) && useSelectedList.length > 0) {
              const idsSelected = useSelectedList.map((item) => item.id);
              setSelectedEmployeeIds(idsSelected);
              setSearchedEmployees(useSelectedList);
              // Merge with existing allEmployees instead of replacing (needed for CourseEmployeeInfo)
              const existingIds = new Set(allEmployees.map(e => e.id));
              const newEmployees = useSelectedList.filter(e => !existingIds.has(e.id));
              if (newEmployees.length > 0) {
                setAllEmployees([...allEmployees, ...newEmployees]);
              } else if (allEmployees.length === 0) {
                // If allEmployees is empty, set it to the selected list
                setAllEmployees(useSelectedList);
              }
              setParamsListEmployee({
                ...paramsListEmployee,
                id_selected_list: JSON.stringify(idsSelected),
                id_exception_list: null,
                no_data: 0,
                isPaging: 0,
              });
              // Update form field
              setValue("employees", useSelectedList.map((i) => i.name).join(", "));
            }
          } catch (e) {
            console.error("Error parsing useSelectedList:", e);
          }
        }
      };

      // Use a small delay to ensure this runs after any reset operations
      const timeoutId = setTimeout(() => {
        checkAndUpdateSelectedEmployees();
      }, 100);

      // Also check when window gets focus or becomes visible (user returns to tab/page)
      const handleFocus = () => {
        setTimeout(() => {
          checkAndUpdateSelectedEmployees();
        }, 100);
      };

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setTimeout(() => {
            checkAndUpdateSelectedEmployees();
          }, 100);
        }
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("focus", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }
  }, [location.pathname, location.key, courseId, isCreatePage]);

  // Additional check: if selectedEmployeeIds is empty but sessionStorage has data, load it
  // This runs after allEmployees is loaded to ensure we have the full employee list
  useEffect(() => {
    if (!isCreatePage && courseId && selectedEmployeeIds.length === 0 && allEmployees.length > 0) {
      const useSelectedListJson = sessionStorage.getItem("useSelectedList");
      if (useSelectedListJson) {
        try {
          const useSelectedList = JSON.parse(useSelectedListJson);
          if (useSelectedList && Array.isArray(useSelectedList) && useSelectedList.length > 0) {
            const idsSelected = useSelectedList.map((item) => item.id);
            setSelectedEmployeeIds(idsSelected);
            setSearchedEmployees(useSelectedList);
            // Merge with existing allEmployees instead of replacing
            const existingIds = new Set(allEmployees.map(e => e.id));
            const newEmployees = useSelectedList.filter(e => !existingIds.has(e.id));
            if (newEmployees.length > 0) {
              setAllEmployees([...allEmployees, ...newEmployees]);
            }
            setParamsListEmployee({
              ...paramsListEmployee,
              id_selected_list: JSON.stringify(idsSelected),
              id_exception_list: null,
              no_data: 0,
              isPaging: 0,
            });
            setValue("employees", useSelectedList.map((i) => i.name).join(", "));
          }
        } catch (e) {
          console.error("Error parsing useSelectedList:", e);
        }
      }
    }
  }, [selectedEmployeeIds.length, allEmployees.length, courseId, isCreatePage]);

  useEffect(() => {
    setValue("document_id", documentOptions?.find((i) => {
      if (i) return i.value === dataCourse?.result?.data?.document_id;
    }));
  }, [dataCourse?.result?.data, documentOptions])

  const onSubmit = async (data) => {
    if (!data.document_id || !data.document_id.value) {
      setValidateDocument(true);
      return;
    }

    if (!data.is_required_all) {
      data.employees = selectedEmployeeIds;
    }
    const payload = {
      is_individual: true,
      target_group_id: data.target_group_id,
      is_required_all: +data.is_required_all,
      effective_time: data.effective_time,
      target_renewal_cycle: data.target_renewal_cycle,
      document_id:
        data.document_id && data.document_id.value
          ? data.document_id.value
          : null,
      sub_list: [],
      group_type: data.group_type,
    };

    if (data.effective_time == 2) {
      payload.validity_from = moment(data.validity_from).isValid()
        ? moment(data.validity_from).format("YYYY-MM-DD")
        : "";
      payload.validity_to = moment(data.validity_to).isValid()
        ? moment(data.validity_to).format("YYYY-MM-DD")
        : "";
    }

    if (!data.is_required_all) {
      payload.user_selected_list = selectedEmployeeIds;
      payload.sub_list = [];
    } else {
      payload.sub_list = [];
      payload.user_selected_list = [];
    }

    if (isCreatePage) {
      mutationCreateCourse.mutate(payload);
      return;
    }

    mutationUpdateCourse.mutate({ ...payload, id: courseId });
  };

  const handleSelectEmployee = () => {
    if (courseId && !isCreatePage) {
      history.push(`/admin/individual-training/assign-training`, {
        ...getValues(),
        courseId,
      });
      sessionStorage.setItem(
        "useSelectedList",
        JSON.stringify(
          allEmployees
            .filter((employee) => selectedEmployeeIds.includes(employee.id))
            .map((employee) => ({ id: employee.id, name: employee.name }))
        )
      );
      return;
    }

    history.push(`/admin/individual-training/assign-training`, {
      ...getValues(),
      courseId,
    });
  };

  const handelCourseChange = (data) => {
    setValue("document_id", data);
    setValidateDocument(false);
    // if (data?.value) {
    //   setIsClearDocument(false);
    // }
  };

  useEffect(() => {
    // Don't clear selectedEmployeeIds if we have data from sessionStorage
    // This prevents clearing data that was loaded from sessionStorage on create page
    if (isCreatePage) {
      const dataCreateCourseIndividualJson = sessionStorage.getItem("dataCreateCourseIndividual");
      if (!dataCreateCourseIndividualJson && !watch("is_required_all")) {
        // Only clear if no sessionStorage data exists and is_required_all is false
        setSelectedEmployeeIds([]);
      }
    } else {
      // For edit page, clear if is_required_all changes to false and no employees selected
      if (!watch("is_required_all") && selectedEmployeeIds.length === 0) {
        setSelectedEmployeeIds([]);
      }
    }

    setParamsListEmployee({
      page: 1,
      limit: PER_PAGE_DEFAULT_EMPLOYEES,
      sortType: null,
      no_data: watch("is_required_all") ? 0 : 1,
      isPaging: watch("is_required_all") ? 1 : 0,
    });
  }, [watch("is_required_all"), isCreatePage, selectedEmployeeIds.length]);

  // auto refresh detail data. if campus_status === 2
  useEffect(() => {
    const interval = setInterval(async () => {
      if (detailCourseIndividual?.campus_status === 2) {
        await refetch();
      }
    }, 30000);
  }, [detailCourseIndividual]);

  useEffect(() => {
    if (listTargetGroup?.result?.data) {
      const targetGroups = [...listTargetGroup.result.data].sort(
        (a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)
      );
      setTargetGroups(targetGroups);
    }
  }, [listTargetGroup?.result?.data]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "40px" }}>
      {(isLoadingDetailCourse || isLoadingListDocument) && <FullPageLoader />}
      <Card overflowX={"hidden"} pb="0px">
        <CardHeader p="6px 0px 20px 0px">
          <Flex justifyContent={"space-between"}>
            <Text fontSize="xl" fontWeight="bold">
              {t("pages.individual.individual_edit_title")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex gap={"10px"} align={"center"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.position_group")}
                </Text>
              </Box>
              <Box width={"50%"}>
                <ESelect
                  name="target_group_id"
                  placeholder={t("common.option.placeholder")}
                  control={control}
                  dataSelect={targetGroups.map((option) => {
                    return { id: option.id, name: t(option.name) };
                  })}
                />
              </Box>
            </Flex>

            <Flex gap={"10px"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.document")}
                </Text>
              </Box>
              <Box width={"50%"}>
                <ESelectWithSearch
                  name="document_id"
                  control={control}
                  options={documentOptions}
                  placeholder={t("pages.course_form.document_placeholder")}
                  onChange={handelCourseChange}
                />
                {validateDocument && (
                  <Text fontSize={14} marginTop={1} color={"red.500"}>
                    {t("messages.required")}
                  </Text>
                )}
              </Box>
            </Flex>

            <Flex gap={"10px"} mb={"10px"} mt={"20px"}>
              <Box w={"15%"} mt={"8px"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.valid_time")}
                </Text>
              </Box>
              <Flex width={"50%"}>
                <Box w={"150px"} mt={"8px"}>
                  <ERadioGroup
                    name="effective_time"
                    control={control}
                    radioData={RADIO_DATA_VALID_PERIOD}
                  />
                </Box>
                <Box ml={"10px"} w={"40%"}>
                  <EDateRangePicker
                    disabled={watch("effective_time") !== "2"}
                    setValue={setValue}
                    control={control}
                    name="validity_from"
                    getValues={getValues}
                    reset={reset}
                    placeholder="YYYY/MM/DD"
                    watch={watch}
                    dateFormat={FORMAT_DATE}
                  />
                </Box>
                <Box ml={"10px"} mt={"8px"}>
                  ~
                </Box>
                <Box ml={"10px"} w={"40%"}>
                  <EDateRangePicker
                    disabled={watch("effective_time") !== "2"}
                    setValue={setValue}
                    name="validity_to"
                    control={control}
                    getValues={getValues}
                    reset={reset}
                    placeholder="YYYY/MM/DD"
                    watch={watch}
                    dateFormat={FORMAT_DATE}
                  />
                </Box>
              </Flex>
            </Flex>

            <Flex gap={"10px"} align={"center"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.required_all")}
                </Text>
              </Box>
              <Box width={"50px"} mt={"10px"}>
                <ECheckbox
                  disabled
                  name="is_required_all"
                  control={control}
                  onChange={onchangeAllCompany}
                />
              </Box>
              <Box w={"100px"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.renew")}
                </Text>
              </Box>
              <Box width={"30%"}>
                <ERadioGroup
                  name="target_renewal_cycle"
                  placeholder=""
                  control={control}
                  radioData={RADIO_DATA_TARGET_RENEWAL_CYCLE}
                />
              </Box>
              {/* </Flex> */}
            </Flex>

            {/* <Flex gap={"10px"} mb={"10px"}>
              <Box w={"15%"} mt={"8px"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.label.employees")}
                </Text>
              </Box>
              <Box width={"50%"}>
                <ETextarea
                  name="employees"
                  placeholder=""
                  control={control}
                  readonly
                />
              </Box>
              <Box mt={"8px"}>
                <PrimaryButton
                  text={t("pages.individual.member_confirmation")}
                  onClick={handleSelectEmployee}
                />
              </Box>
            </Flex> */}

            <CourseEmployeeInfo
              handleFirstBtn={handleSelectEmployee}
              textFirstBtn={t("pages.individual.member_confirmation")}
            />
            <Flex justifyContent={"end"} gap={"20px"} pb={"10px"}>
              {isCreatePage ? (
                <PrimaryButton
                  text={t("pages.course_form.register")}
                  type="submit"
                  isLoading={mutationCreateCourse.isLoading}
                />
              ) : (
                <Flex gap={"20px"}>
                  {dataCourse?.result?.data?.campus_status == 2 ? (
                    <DisableButton
                      text={t("pages.course_form.save_changes")}
                      type="submit"
                      isLoading={mutationUpdateCourse.isLoading}
                    />
                  ) : (
                    <PrimaryButton
                      text={t("pages.course_form.save_changes")}
                      type="submit"
                      isLoading={mutationUpdateCourse.isLoading}
                    />
                  )}
                  <DeleteButton
                    text={t("pages.course_form.un_assign_course")}
                    onClick={() => open("modalAlert")}
                  />
                </Flex>
              )}
              <SecondaryButton
                text={t("pages.course_form.cancel")}
                onClick={() => history.go(-1)}
              />
            </Flex>
          </form>
          {!isCreatePage && (
            <Flex justifyContent={"center"} pb={"20px"}>
              {dataCourse?.result?.data?.campus_status == 2 ? (
                <DisableButton
                  text={t("pages.course_form.pushing")}
                  isLoading={"true"}
                />
              ) : (
                <GreenButton
                  text={t("pages.course_form.cloud_campus_status_1")}
                  onClick={() => onPushManual(courseId)}
                  disabled={dataCourse?.result?.data?.campus_status === 4}
                />
              )}
            </Flex>
          )}
        </CardBody>
      </Card>
      <ModalAlert
        isOpen={modalState.modalAlert}
        onClose={() => close("modalAlert")}
        title={t("pages.course_form.delete_course")}
        content={t("pages.course_form.confirm_delete_course")}
        textBtnLeft={t("pages.course_form.cancel")}
        textBtnRight={t("pages.course_form.delete")}
        onClickBtnLeft={() => close("modalAlert")}
        onClickBtnRight={() => mutationDeleteCourse.mutate(courseId)}
      />
    </Flex>
  );
};

export default IndividualCourseForm;
